import { Certificate, CLEPeriod, ICreditClassData } from '@/@types/client-api';
import { parseDate } from '@/utils/helpers';
import { useCallback, useMemo } from 'react';
import { CompliancePeriodValue } from '@/components/ui/CreditTracker/util/constants';
import { roundCredits } from '@/components/ui/CreditTracker/util/TrackerHelpers';

export type CreditFormHookProps = {
  certificate: Certificate | null;
  selectedCertificatesCarryover: Certificate[];
  creditTypes: ICreditClassData[] | undefined;
  selectedPeriod?: CLEPeriod;
};

const useCreditFormHook = ({
  certificate,
  selectedCertificatesCarryover,
  creditTypes,
  selectedPeriod
}: CreditFormHookProps) => {
  const parsedDateCompleted = useMemo(
    () => parseDate(certificate?.dateCompleted ?? ''),
    [certificate?.dateCompleted]
  );

  const programNameOptions = useMemo(
    () =>
      selectedCertificatesCarryover
        ?.filter(certificate => certificate.programName != null && (certificate.id ?? -1) >= 0)
        ?.sort((x, y) => {
          const a = new Date(x.dateCompleted ?? 0);
          const b = new Date(y.dateCompleted ?? 0);
          return a.getTime() - b.getTime();
        })
        ?.map(certificate => ({
          value: certificate.programName!,
          label: certificate.programName!
        })),
    [selectedCertificatesCarryover]
  );

  const formattedAvailableCreditTypes = useMemo(() => {
    return (
      creditTypes?.map(item => {
        return {
          label: item.longDescription,
          value: item.wmsCreditTypePK
        };
      }) || []
    );
  }, [creditTypes]);

  //TODO: find out if this comes from API or is hardcoded
  // Note: the values of these formats should match the old site
  const programFormatOptions = useMemo(() => {
    return [
      {
        label: 'Live In-Person',
        value: 'Live'
      },
      {
        label: 'Live Webcast',
        value: 'Live-webcast'
      },
      {
        label: 'On-Demand',
        value: 'On-Demand'
      }
    ];
  }, []);

  const compliancePeriodOptions: { label: string; value: CompliancePeriodValue }[] = useMemo(() => {
    return [
      { label: 'Previous compliance period', value: 'previous' },
      { label: 'Current compliance period', value: 'current' },
      { label: 'Next compliance period', value: 'next' }
    ];
  }, []);

  const carryoverPeriod: CompliancePeriodValue | null = useMemo(() => {
    const dateCompleted = certificate?.dateCompleted;
    if (!dateCompleted) {
      return null;
    }

    const timestamp = new Date(dateCompleted).getTime();
    const startDate = new Date(
      selectedPeriod?.startDate ?? selectedPeriod?.defaultStartDate ?? 0
    ).getTime();

    const endDate = new Date(
      selectedPeriod?.endDate ?? selectedPeriod?.defaultEndDate ?? 0
    ).getTime();

    if (timestamp < startDate) {
      return 'previous';
    } else if (timestamp > endDate) {
      return 'next';
    }

    return 'current';
  }, [
    certificate?.dateCompleted,
    selectedPeriod?.defaultEndDate,
    selectedPeriod?.defaultStartDate,
    selectedPeriod?.endDate,
    selectedPeriod?.startDate
  ]);

  const getSelectedCreditTypeLabel = useCallback(
    (value: string) => formattedAvailableCreditTypes?.find(a => a.value == value)?.label ?? '',
    [formattedAvailableCreditTypes]
  );

  const getTotalCredits = useCallback(() => {
    if (certificate && certificate.creditTypes) {
      return certificate.creditTypes.reduce(
        (acc, credit) => roundCredits(acc + (credit.creditIssued ?? 0)),
        0
      );
    }
    return 0;
  }, [certificate]);

  return {
    parsedDateCompleted,
    programNameOptions,
    formattedAvailableCreditTypes,
    programFormatOptions,
    compliancePeriodOptions,
    carryoverPeriod,
    getTotalCredits,
    getSelectedCreditTypeLabel
  };
};

export default useCreditFormHook;
